import React from 'react';
import RadioButtons from 'jsx/elements/inputs/RadioButtons';
import Flex from 'jsx/layout/Flex';
import TextInput from 'jsx/elements/inputs/TextInput';
import { formatNumber } from 'utils/number';
import Warning from 'jsx/elements/messages/Warning';

export const LoanFreezeApplicationForm = ({ form, onUpdate = null, onBlur: userOnBlur = null }) => {
  const onBlur = () => userOnBlur(); // To make sure events dont pass through since we dont need them.
  const preview = typeof onUpdate !== 'function';

  return (
    <div className="form">
      <Conditions
        months={form.applicantFreezeMonthsRequested}
        resolutions={form.applicantHasUsedOtherResolutions}
        onUpdate={onUpdate}
        preview={preview}
      />

      <div className="gutter-top">
        <TextAreaField
          question="Umsögn umsóknaraðila vegna skilyrða greiðslufrests:"
          value={form.applicantComment}
          onChange={preview ? null : val => onUpdate('applicantComment', val)}
          onBlur={onBlur}
        />
      </div>

      <div className="gutter-top">
        <TextAreaField
          question="Umfjöllun og rekstrarhorfur umsóknaraðila fyrir árið 2020:"
          value={form.applicantForecastComment}
          onChange={preview ? null : val => onUpdate('applicantForecastComment', val)}
          onBlur={onBlur}
        />
      </div>

      <div className="gutter-top">
        <Revenue revenue={form.revenues} preview={preview} onUpdate={onUpdate} onBlur={onBlur} />
      </div>

      <hr className="gutter-top gutter-bottom" />

      <div className="grid gutter collapse720">
        <div className="col s1of3">
          <TextField
            question="Nafn tengiliðs"
            onBlur={onBlur}
            onChange={preview ? null : onUpdate}
            name="contact-name"
            value={form.contact.name}
          />
        </div>

        <div className="col s1of3">
          <TextField
            question="Netfang tengiliðs"
            onBlur={onBlur}
            onChange={preview ? null : onUpdate}
            name="contact-email"
            value={form.contact.email}
          />
        </div>

        <div className="col s1of3">
          <TextField
            question="Staðfest af hálfu umsóknaraðila"
            onBlur={onBlur}
            onChange={preview ? null : onUpdate}
            name="contact-phoneNumber"
            value={form.contact.phoneNumber}
          />
        </div>
      </div>
    </div>
  );
};

const Conditions = React.memo(({ onUpdate, preview, resolutions, months }) => {
  return (
    <div className="grid gutter collapse720 gutter-top">
      <div className="col s1of2 gutter-top">
        <RadioField
          question="Hefur þú nýtt þér viðeigandi úrræði stjórnvalda vegna heimsfaraldursins sem þínu fyrirtæki stendur til boða?"
          name="applicantHasUsedOtherResolutions"
          choices={otherResolutionValues}
          onChange={
            preview
              ? null
              : val => {
                  onUpdate('applicantHasUsedOtherResolutions', val === 'true');
                }
          }
          value={String(resolutions)}
        />
      </div>
      <div className="col s1of2 gutter-top">
        <RadioField
          question="Í hversu langan tíma er óskað eftir frystingu lána?"
          name="applicantFreezeMonthsRequested"
          choices={monthCountValues}
          onChange={preview ? null : val => onUpdate('applicantFreezeMonthsRequested', Number(val))}
          value={String(months)}
        />
      </div>
    </div>
  );
});

const Revenue = React.memo(({ revenue, preview, onUpdate, onBlur }) => {
  const byYear = revenue.reduce((res, item) => {
    const year = new Date(item.date).getUTCFullYear();
    if (res[year] === undefined) {
      res[year] = [];
    }
    res[year].push(item);
    return res;
  }, {});

  const sortedYears = React.useMemo(
    () => Object.keys(byYear).sort((b, a) => (b > a ? -1 : a > b ? 1 : 0)),
    []
  );

  return (
    <>
      <div className={preview ? 'text-semibold' : ''}>
        Upplýsingar um tekjur fyrirtækisins (í þ.kr. án virðisaukaskatts)
      </div>

      {!preview && (
        <div className="half-spacing-top">
          <Warning
            messageType="info"
            messageBody="Slá skal inn rekstrartekjur hvers mánaðar í þúsundum króna án VSK. Summa talnanna fyrir árið 2019 ætti því að stemma við Rekstrartekjur í ársreikningi (ef uppgjörsár er frá jan-des)."
          />
        </div>
      )}

      {sortedYears.map(year => (
        <Flex key={year} className="gutter-bottom">
          <div className="text-semibold gutter-right half-spacing-top">{year}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {byYear[year].map(revenue => (
              <div
                style={{ width: 140 }}
                key={revenue.revenueID}
                className="gutter-right half-spacing-top"
              >
                <div style={{ marginBottom: '0.75rem' }} className={preview ? 'text-semibold' : ''}>
                  {getMonth(revenue.date)}
                </div>
                {preview ? (
                  <div>{formatNumber(Number(revenue.value))}</div>
                ) : (
                  <TextInput
                    name={`revenue-${revenue.revenueID}`}
                    value={revenue.value || ''}
                    onChange={(_, val) => onUpdate(`revenue-${revenue.revenueID}`, val)}
                    onBlur={onBlur}
                  />
                )}
              </div>
            ))}
          </div>
        </Flex>
      ))}
    </>
  );
});

const Question = ({ children, preview }) => (
  <div className={`half-spacing-bottom ${preview && 'text-semibold'}`}>{children}</div>
);

const RadioField = ({ name, choices, onChange, value, question }) => {
  if (!onChange) {
    return (
      <>
        <Question preview>{question}</Question>
        <div>{getChoiceText(choices, value)}</div>
      </>
    );
  }

  return (
    <>
      <Question>{question}</Question>
      <RadioButtons
        horizontal
        name={name}
        values={choices}
        onChange={onChange}
        selectedValue={value}
      />
    </>
  );
};

const TextField = ({ onChange, onBlur, value, name, question }) => {
  if (!onChange) {
    return (
      <>
        <Question preview>{question}</Question>
        <div>{value || '-'}</div>
      </>
    );
  }
  return (
    <>
      <Question>Netfang tengiliðs</Question>
      <TextInput onBlur={onBlur} onChange={onChange} name={name} value={value} />
    </>
  );
};

const TextAreaField = ({ value, onChange, question, onBlur }) => {
  if (!onChange) {
    return (
      <>
        <Question preview>{question}</Question>
        <p>{value || '-'}</p>
      </>
    );
  }

  const change = e => onChange(e.target.value);
  return (
    <>
      <Question>{question}</Question>
      <textarea value={value || ''} onChange={change} onBlur={onBlur} />
    </>
  );
};

// -----------------------------------  Helpers -----------------------------------

let cache = {};
const getMonth = date => {
  if (cache[date]) return cache[date];
  const val = months[new Date(date).getUTCMonth()];
  cache[date] = val;
  return val;
};

const getChoiceText = (choices, value) => {
  if (String(value) == 'null') return '';
  return choices.find(c => String(c.value) === String(value)).label;
};

// -----------------------------------  Constants -----------------------------------
const otherResolutionValues = [
  { label: 'Já', value: 'true' },
  { label: 'Nei', value: 'false' }
];

const monthCountValues = [
  { label: '3 mánuði', value: '3' },
  { label: '4 mánuði', value: '4' },
  { label: '5 mánuði', value: '5' },
  { label: '6 mánuði', value: '6' }
];

const months = [
  'Janúar',
  'Febrúar',
  'Mars',
  'Apríl',
  'Maí',
  'Júní',
  'Júlí',
  'Ágúst',
  'September',
  'Október',
  'Nóvember',
  'Desember'
];
